import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Whether the component is being taken off screen, set as soon as React starts removing it: in a
 * layout effect's cleanup, which runs before the layout effects of whatever replaces it in the same
 * commit, and before its own passive cleanups.
 *
 * Also set while a Suspense boundary hides the component behind its fallback, since React runs the
 * same cleanup then, and cleared when it shows again. The component is still mounted meanwhile, so
 * pair this with another signal where that difference matters (see `useHasLeftPage`).
 *
 * Reset on mount as well, since StrictMode tears effects down and sets them up again.
 */
export function useIsUnmountingRef(): RefObject<boolean> {
  const isUnmounting = useRef(false);
  useLayoutEffect(() => {
    isUnmounting.current = false;
    return () => {
      isUnmounting.current = true;
    };
  }, []);
  return isUnmounting;
}
